import React, { useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import moment from "moment-timezone";
import Radio from '@material-ui/core/Radio';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { RefreshOutlined } from '@material-ui/icons';
import LaoBarChart from './Charts/LaoBarChart';
import { useTranslation } from "react-i18next"
import { useState } from 'react';
import { laoGetCharts as laoGetChartsPlanning } from 'reduxLib/services/LaoServices/laoChartService';
import { useDispatch, useSelector } from 'react-redux';
import useSetQuery, { useMultiSetQuery } from 'customHooks/useQuery';
import { useDeepCompareEffect } from 'react-use';
import { useLAOGlobalContext } from 'context/LAOGlobalContext';
import esLocale from "moment/locale/es"; // Do not remove these imports needed for chart translation 
import ptLocale from "moment/locale/pt"; // Do not remove these imports needed for chart translation 

const useStyles = makeStyles(theme => ({
  planningChartContainer: {
    background: theme.palette.primary.base,
    borderRadius: theme.spacing(1.5),
    padding: `${theme.spacing(3)}px ${theme.spacing(8)}px ${theme.spacing(3)}px ${theme.spacing(8)}px`,
    gap: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: `${theme.spacing(3)}px`,
    }
  },
  planningSpining: {
    pointerEvents: "none",
    height: '0.9em',
    animation: "App-logo-spin infinite 1s linear !important"
  },
  planningSpin: {
    height: '0.9em',
    cursor: "pointer",
  },
  planningChartCard: {
    maxWidth: '100%',
    minWidth: '100%'
  },
  planningActive: {
    opacity: "100%",
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'center'
    }
  },
  planningFaded: {
    opacity: "30%",
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'center'
    }
  },
  weeklyFaded: {
    opacity: "30%",
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  },
  planningBlueSquare: {
    height: 12,
    width: 12,
    backgroundColor: theme.palette.chartColors.healthy,
    display: 'inline-block'
  },
  planningTextContent: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    width: 'max-content',
    display: 'inline-block'
  },
  planningVolume: {
    display: 'flex',
    justifyContent: 'start',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-evenly'
    }
  },
  planningFrequency: {
    display: 'flex',
    justifyContent: 'end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between'
    }
  },
  volumefrequency: {
    display: 'flex',
    justifyContent: 'start',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between'
    }
  },
  planningVolumeType: {
    display: 'flex',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  },
  planningDaily: {
    display: 'flex',
    justifyContent: 'end',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-start'
    }
  },
  planningWeekly: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-start'
    }
  },     
  planningReload: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-end'
    }
  },
  planningLoadingWindow: {
    height: '36vh'
  }
}));

const VolumeTypeOption = (activeVolumePlanning, lang, classes, setChartTypesPlanning, t) => {
  const baseVolumeTypes = [
    { key: 'cases', testId: 'cases', sm: 3 },
    { key: 'pallets', testId: 'pallets', sm: lang === 'en' ? 3 : 4 },
    { key: 'volume', testId: 'volume', sm: lang === 'en' ? 3 : 4 },
  ];

  const extendedVolumeTypes = activeVolumePlanning.type === 'loadPlan'
    ? [...baseVolumeTypes, { key: 'containers', testId: 'containers', sm: lang === 'en' ? 3 : 4 }]
    : baseVolumeTypes;

  return <>
    {extendedVolumeTypes.map(({ key, testId, sm }) => (
      <Grid
        key={key}
        item
        sm={sm}
        md={4}
        lg={3}
        className={classes.planningVolumeType}
        data-testid={`${testId}-grid`}
      >
        <Typography variant="h6" data-testid={`${testId}-title`}>
          <Radio
            checked={activeVolumePlanning.volumeType === key}
            onClick={() => setChartTypesPlanning({ volumeType: key })}
            inputProps={{ 'data-testid': `radio_${testId}` }}
          />
          {t(key)}
        </Typography>
      </Grid>
    ))}
  </>
}

const LaoPlanningChart = ({ ...rest }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [{ chartType: chartTypePlanning }, setParamsPlanning] = useSetQuery();
  const setMultiQueryPlanning = useMultiSetQuery();
  const chartTypes = chartTypePlanning?.split("_")
  const [activeVolumePlanning, setActiveVolumePlanning] = useState({
    type: chartTypes?.[0] || "drp",
    subtype: chartTypes?.[1] || "daily",
    volumeType: chartTypes?.[2] || 'cases'
  });

  const laoChartsDataPlanning = useSelector(({ laoCharts }) => laoCharts);
  const [chartDataPlanning, setChartData] = useState([]);
  const isMobileLao = useMediaQuery(useTheme().breakpoints.down('xs'));

  const {
    locations,
    BU,
    language: lang
  } = useLAOGlobalContext();
  moment.locale(lang);

  const subtypesPlanning = {
    drp: {
      daily: "EXPORT_DRP_COUNT_DAILY",
      weekly: "EXPORT_DRP_COUNT_WEEKLY"
    },
    loadPlan: {
      daily: "EXPORT_LOAD_PLAN_COUNT_DAILY",
      weekly: "EXPORT_LOAD_PLAN_COUNT_WEEKLY"
    }
  };
  const subtype = subtypesPlanning[activeVolumePlanning.type][activeVolumePlanning.subtype];

  const volumeTypeMapping = {
    cases: 'casesCount',
    pallets: 'palletCount',
    volume: 'volumeCount',
    containers: 'containerCount'
  }

  const chartPropsPlanning = {
    showTotal: true,
    yLabel: '',
    excludedFilters: {},
    height: 300,
    data: chartDataPlanning,
    startDate: 'startDate',
    xKey: 'formattedStarDate',
    keys: [volumeTypeMapping[activeVolumePlanning.volumeType]],
    yKey: volumeTypeMapping[activeVolumePlanning.volumeType],
    themeType: 'dark',
    loading: false,
    showXAxisLabel: !isMobileLao,
    setDetailsData: true,    
    ...rest
  };

  const setDetailsPlanning = (data) => {
    let dateFilter = chartTypes?.[0] === "drp"
      ? {
        availabilityDateRange: {
          startTime: data?.startDate,
          endTime: data?.endDate
        }
      }
      : {
        loadDateRange: {
          startTime: data?.startDate,
          endTime: data?.endDate
        }
      };

    setMultiQueryPlanning({
      laoFilters: JSON.stringify(dateFilter),
      tableType: chartTypes?.[0] === 'drp' ? "exportDRPDetails" : "exportLoadPlanDetails",
      isHyperlink: true
    });
  };

  const getChartsPlanning = () => {
    const controller = new AbortController();
    dispatch(laoGetChartsPlanning({
      page: 'exportPlanning',
      controller,
      subtype,
      body: {
        businessUnits: BU,
        originSites: locations,
        startDate: new Date().toISOString().split("T")[0]
      }
    }));
  };

  const setChartTypesPlanning = (values) => {
    const updatedChartTypes = {
      ...activeVolumePlanning,
      ...values
    };
    setActiveVolumePlanning(updatedChartTypes);
    setParamsPlanning({
      key: "chartType",
      value: `${updatedChartTypes.type}_${updatedChartTypes.subtype}_${updatedChartTypes.volumeType}`
    });
  };

  useEffect(() => {
    const data = laoChartsDataPlanning[subtype];
    if (data?.status?.status === "success" && data?.data?.length) {
      let processedData = data?.data?.map(d => {
        return {
          ...d,
          formattedStarDate: moment(d[chartPropsPlanning.startDate], "YYYY-MM-DD").format("DD/MM ddd")
        }
      });
      setChartData(processedData);
    } else {
      setChartData([])
    }
  }, [laoChartsDataPlanning[subtype]]);

  useDeepCompareEffect(() => {
    getChartsPlanning();
  }, [BU, locations, activeVolumePlanning, lang]);
  // Can be enabled in future to optimize no of api calls 
  // but loading animation will be removed in the charts
  // }, [BU, locations, activeVolumePlanning.type, activeVolumePlanning.subtype, lang]);

  return (
    <Grid container className={classes.planningChartContainer} data-testid="lao-planningChartcontainer">
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={12} className={classes.planningVolume}>
          <Grid item sm={12} md={6} lg={lang == 'en' ? 1 : 2}>
            <Typography data-testid="exportDRP" variant="h6" className={activeVolumePlanning.type === "drp" ? classes.planningActive : classes.planningFaded}
              onClick={() => setChartTypesPlanning({ type: 'drp', subtype: "daily" })}>{isMobileLao ? t('exportDRPMobile') : t('exportDRP')}</Typography>
          </Grid>
          <Grid item sm={12} md={6} lg={lang == 'en' ? 1 : 4}>
            <Typography data-testid="exportLoadPlan" variant="h6" className={activeVolumePlanning.type === "loadPlan" ? classes.planningActive : classes.planningFaded}
              onClick={() => setChartTypesPlanning({ type: 'loadPlan', subtype: "daily" })}>{isMobileLao ? t('exportLoadPlanMobile') : t('exportLoadPlan')}</Typography>
          </Grid>
        </Grid>
        </Grid>
        <Grid container item spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={6} className={classes.volumefrequency}>
          {VolumeTypeOption (activeVolumePlanning, lang, classes, setChartTypesPlanning, t)}
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={6} className={classes.planningFrequency}>
          <Grid item sm={3} md={5} lg={4} className={classes.planningDaily}>
            <Typography variant="h6">
              <Radio
                checked={activeVolumePlanning.subtype === "daily"}
                onClick={() => setChartTypesPlanning({ subtype: 'daily' })}
                inputProps={{ 'data-testid': 'radio_daily' }}
              />
              {t('daily')}
            </Typography>
          </Grid>
          <Grid item sm={lang === 'en' ? 3 : 4} md={5} lg={4} className={classes.planningWeekly}>
            <Typography variant="h6">
              <Radio
                checked={activeVolumePlanning.subtype === "weekly"}
                onClick={() => setChartTypesPlanning({ subtype: 'weekly' })}
                inputProps={{ 'data-testid': 'radio_weekly' }}
              />
              {t('weekly')}
            </Typography>
          </Grid>
          <Grid item sm={3} md={2} lg={1} className={classes.planningReload}>
            <IconButton onClick={getChartsPlanning} data-testid="refreshbtn" >
              <RefreshOutlined fontSize='small' className={laoChartsDataPlanning[subtype]?.loading ? classes.planningSpining : classes.planningSpin} />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>


      <Grid container sm={12} md={12} lg={12}>
        <div className={classes.planningChartCard}>
          {
            laoChartsDataPlanning[subtype]?.loading
              ?
              <div className={classes.planningLoadingWindow}>
                <Typography color="primary">
                  {t("please_wait")}
                </Typography>
              </div>
              :
              < LaoBarChart {...chartPropsPlanning} setDetails={setDetailsPlanning} />
          }
        </div>
        <div>
          <div className={classes.planningBlueSquare} />
          <div className={classes.planningTextContent}>{t(activeVolumePlanning.volumeType)}</div>
        </div>
      </Grid>
    </Grid>
  );
}

export default LaoPlanningChart